import { FilterQuery } from './filter-query.model';
import { MapGeometry } from './geometry.model';
import {
  ParsedUploadPlantation,
  PlantationValidationErrors
} from './parsed-upload-plantation.model';
import { ChipVariant } from '../shared/chip-label/chip-variant.type';
import { PLANTATION_LIST_TABLE_COLUMNS } from '../constants/table-columns.const';
import { ScreenEnum } from '../enums/screens.enum';
import { ColumnDefinition } from './column-definition.model';
import { PagedResponse } from './paged-response.model';
import { AgriTraceFile } from './risk-mitigation-v2.model';
import { Settings, SettingsPayload } from './settings.model';

export interface Plantation {
  _id?: string;
  plantation_code: string;
  plantation_name: string;
  area_calculated: string;
  /** Formatted to two decimals for the table; numeric from the API. */
  average_monthly_yield?: number | string | null;
  country: string;
  adm_1: string;
  adm_2: string;
  adm_3: string;
  adm_4: string;
  date_created: string;
  data_source: string;
  field_team_code?: string;
  /** AgriTrace inventory includes plantations saved without a mapped location. */
  _geo?: string | null;
  risks?: Risk[];
  isExpanded?: boolean;
  checked?: boolean;
  /** Only populated when the stored geometry can be plotted. */
  mapData?: MapData;
  risk_self_overlap_v1_value?: boolean;
  risk_tree_cover_loss_v1_value?: boolean;
  risk_wdpa_v1_value?: boolean;
  has_any_risk_value?: boolean;
  T?: string;
  _docid?: string | null;
  _createdbyname?: string | null;
  survey_risks?: SurveyRisk[];
  survey_risks_updated?: SurveyRiskUpdated;
  geoJsonType?: GeoJsonType | string | null;
  risk_rating?: RiskRating | string;
  mitigation_status: string | null;
  // Supplier the plot came from — a Supplier Portal upload or an accepted
  // cross-tenant import. Null on the buyer's own uploads (crd-api AGD-930).
  supplier_name?: string | null;
  mitigation_status_props?: {
    variant: ChipVariant;
    label: string;
  } | null;
  overAllSurveyMaxRiskRatingProps?: {
    overAllSurveyMaxRiskScoreRating: any;
    overAllSurveyMaxRiskRating: any;
    overAllSurveyMaxRiskRatingLabel: string;
    overAllSurveyMaxRiskRatingColor: string;
  } | null;
}

export interface RiskRating {
  compliance: string;
  variant: ChipVariant;
}

export interface GeoJsonType {
  label: string;
  icon: string;
}

export interface SurveyRisk {
  framework: string;
  qid: string;
  risk_score: number;
  category: string;
  question: string;
}

export interface SurveyRiskUpdated {
  [key: string]: SurveyRisk | string | null;
}

export interface RiskOverlapDetail {
  label: string;
  riskType: string;
  description: string;
  areaCalculatedSum?: number;
  plantationCodeCount?: number;
  color: string;
  areas?: any;
}

export interface RiskOverlapRow {
  plantation_code: string;
  plantation_name: string | null;
  area: number | null;
  percentage: number | null;
  mapData?: MapData;
}

export type RiskFilterSelection = Record<string, boolean>;

export interface PlantationTableFilters {
  [key: string]: {
    label: string | null;
    value: any;
    icon?: string | null;
    iconColor?: string;
    type?: string | null;
    showDividerBefore?: boolean;
  }[];
}

export interface AdmFilter {
  country: string;
  adm_1: string;
  adm_2: string;
}

export interface DataSourceFilter {
  data_source: string;
}

export interface PlantationNameFilter {
  plantation_code: string;
  plantation_name: string;
}

export interface RiskLayer {
  id: string;
  name: string;
  dataurl: string;
  color: string;
  type: string;
  checked?: boolean;
  opacity?: number;
}

export interface TileUrlResult {
  risk_id: string;
  xyz_url?: string;
  color?: string;
  opacity?: number;
  error?: string;
}

export interface TileUrlsResponse {
  results: TileUrlResult[];
}

export interface Dataset {
  name: string;
  default_plantations_table: string;
  description: string;
  producerName?: string;
  /**
   * Company that owns this dataset. Nullable, and a string even though the
   * platform's own `company.id` is a number — compare the two coerced.
   */
  agd_company_id?: string | null;
}

// used summary charts
export interface TotalLandArea {
  // Raw country groups can be null or the backend's "Invalid Geometry" sentinel.
  country: string | null;
  area: number;
  totalCount: number;
  totalYield: number;
}

export interface TotalPlot {
  /** `'unmapped'` for AgriTrace plots saved before mapping; null when geometry failed to parse. */
  geometry_type: string | null;
  count: number;
  label?: string;
  icon?: string;
}

export interface TotalMappingEffort {
  day: string;
  plantationsMapped: number;
  totalArea: number;
  data_source: string;
}

export interface OverallRisk {
  riskRating: string;
  riskRatingValue: number;
  label?: string;
  icon?: string;
}

export interface MappingDataQuality {
  overlapDistribution: number;
  overlaps: string;
  color?: string;
  label?: string;
}

export interface GeospatialRiskCounts {
  totalFarms: number;
  [key: string]: number;
}

export interface GeospatialRiskSummary extends Partial<RiskProperty> {
  display_name: string;
}

export interface GeospatialRisksSummary {
  totalFarms: number;
  [key: string]: number | GeospatialRiskSummary;
}

export interface TotalSummary {
  totalPlots: TotalPlot[];
  totalLandAreas: TotalLandArea[];
  totalMappingEfforts: TotalMappingEffort[];
  overallRisks: OverallRisk[];
  mappingDataQuality: MappingDataQuality[];
  geospatialRisksSummary: GeospatialRisksSummary | [];
}

// Used in Questionaire table
export interface Risk {
  category: string;
  total_score: number;
  color?: string; // hex color
  display_name?: string;
}

export interface RiskValueAggregate {
  area_calculated_sum: number;
  plantation_code_count: number;
}

/**
 * The one `compute_source` whose rows AGD-GeoSpatial owns, and so the only
 * rows that address tiles by `risk_function`. Mirrors crd-api, which forwards
 * only these rows to the geospatial service.
 */
export const AGD_GEOSPATIAL_COMPUTE_SOURCE = 'agd-geospatial';

export interface RiskProperty {
  /**
   * Stable storage identifier: the BigQuery column prefix a result is written
   * under, and the mitigation key. Deliberately frozen so those survive a
   * compute upgrade, so it can lag the compute version — a catalog entry
   * displayed as "Deforestation v2" is still stored under `..._v1`.
   */
  name: string;
  /**
   * Identifier of the function that computes this risk. What it dispatches to
   * depends on {@link compute_source}: for `agd-geospatial` rows it is the
   * geospatial tile/compute identifier, but for `bq-udfs` rows it is a
   * BigQuery UDF name that the tile service does not recognise. Resolve it
   * through `getTileRiskId()` rather than reading it directly.
   */
  risk_function?: string | null;
  /**
   * Compute engine behind this risk: `agd-geospatial | bq-udfs | agp-survey`,
   * or null for rows with no compute wiring (e.g. self-overlap). Only
   * `agd-geospatial` rows address tiles by {@link risk_function}.
   */
  compute_source?: string | null;
  display_name: string;
  color: string;
  type: string;
  description: string;
  is_default?: boolean;
  value?: number;
  key?: string;
}
export interface FavoriteFilter {
  id: number;
  dataset_id: number;
  dataset_name: string;
  filters: FilterQuery;
  filter_name: string;
}

export interface GeometryData {
  plantation_code: string;
  /**
   * Unmapped AgriTrace plots are retained in inventory. Null geometry and legacy
   * empty polygons have no map shape, but must not hide the rest of the record.
   */
  _geo?: MapData;
}

export type MapData = MapGeometry;

export interface SiDetails {
  siNumber: string;
  siDetailId: string;
  factoryCode: string;
  contractNumber: string;
  company: Company;
  defaultMode?: string;
  siLinkingMode?: string;
}

export interface SiDetailsQueryParams extends Omit<SiDetails, 'company'> {
  company: string;
}

export interface Company {
  type: string;
  name: string;
}

export interface AgriTraceFilePreview {
  file_url: string;
  status: string;
}

/**
 * Risk mitigation related states
 */

export interface RiskMitigationRisk {
  risk_type: string;
  risk_id: string;
  risk_name: string;
  risk_value: boolean;
  needs_mitigation: boolean;
  updated_date: string | null;
  file_type: string;
  risk_display_name: string;
  status: string | null;
  statusChipProps?: {
    variant: ChipVariant;
    label: string;
  } | null;
}

type PlantationTableColumnKeys = Exclude<
  (typeof PLANTATION_LIST_TABLE_COLUMNS)[number]['field'],
  'actions'
>;

export type PlantationTableColumnsSettings = Record<
  PlantationTableColumnKeys,
  boolean
>;

/**
 * End risk mitigation related states
 */

export interface CrdState {
  plantation: Plantation;
  selectedPlantationList: Plantation[];
  prevPlantationList: Plantation[];
  linkedPlantationList?: Plantation[];
  checkedSelectedPlantationList?: Plantation[];
  pagedPlantationList: PagedResponse<Plantation>;
  pagedRiskValues: PagedResponse<any>;
  pagedRiskValuesModal: PagedResponse<any>;
  // TODO: update type when Questionnaire interface updated
  pagedQuestionnaireList: PagedResponse<any>;
  selectedQuestionnaireList: Plantation[];
  favoritePlantationFilters?: FavoriteFilter[];
  plantationListFilters?: PlantationTableFilters;
  questionnaireListFilters?: PlantationTableFilters;
  selectedPlantationFilters?: PlantationTableFilters;
  riskOverlapDetails?: RiskOverlapDetail[];
  riskLayers: RiskLayer[];
  geometryData: GeometryData[];
  selfOverlapGeometryData: GeometryData[];
  mapStyle: string;
  currentScreen: ScreenEnum;
  geometryDataExport: Plantation[];
  datasets?: Dataset[];
  selectedDataset?: Dataset | null;
  selectedPeriod?: string;
  // TODO: update type
  parsedFileData?: ParsedUploadPlantation[];
  parsedFileErrors?: PlantationValidationErrors;
  uploadFileMappingColumns?: ColumnDefinition[];
  totalSummary?: TotalSummary;
  riskValueAggregates?: RiskValueAggregate[];
  riskProperties?: RiskProperty[];
  siDetails?: SiDetails | null;
  tableColumns: ColumnDefinition[];
  settings?: Map<string, Settings>;
  savedSettings?: SettingsPayload;
  // used in settings estimated yield by country
  countries?: string[];
  plantationUploadFileName?: string;
  bulkSearch?: { key: string; value: string[] };
  riskDataByPlantationCode?: RiskMitigationRisk[];
  allPlantations?: Plantation[];
  selectAllPlantationsActive?: boolean;
  agriTraceFiles?: AgriTraceFile[];
  agriTraceFilePreview?: AgriTraceFilePreview;
  plantationTableColumnSettings?: PlantationTableColumnsSettings;
}

// TODO: need to align with BE to use just page/pageNumber
export const INITIAL_TABLE_PARAMS = {
  page: 1,
  pageNumber: 1,
  pageSize: 30,
  orderBy: '',
  filters: null
};

export const PAGE_SIZE_OPTIONS = [30, 50, 100];

export const INITIAL_PAGED_STATE: PagedResponse<Plantation> = {
  count: 0,
  next: '',
  previous: '',
  num_pages: 0,
  page: 0,
  page_size: 0,
  results: []
};

export const INITIAL_SELECTED_PERIOD = '2022-01-01';

export const INITIAL_CRD_STATE: CrdState = {
  pagedPlantationList: INITIAL_PAGED_STATE,
  pagedQuestionnaireList: INITIAL_PAGED_STATE,
  selectedQuestionnaireList: [],
  pagedRiskValues: INITIAL_PAGED_STATE,
  pagedRiskValuesModal: INITIAL_PAGED_STATE,
  riskLayers: [],
  geometryData: [],
  selfOverlapGeometryData: [],
  mapStyle: '',
  selectedPlantationList: [],
  prevPlantationList: [],
  plantation: {
    plantation_code: '',
    plantation_name: '',
    area_calculated: '',
    country: '',
    adm_1: '',
    adm_2: '',
    adm_3: '',
    adm_4: '',
    date_created: '',
    data_source: '',
    field_team_code: '',
    risks: [],
    mitigation_status: null
  },
  currentScreen: ScreenEnum.DASHBOARD,
  geometryDataExport: [],
  tableColumns: PLANTATION_LIST_TABLE_COLUMNS
};
